import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from 'src/app/core/services/admin.service';
import { MSTeamsSharedService } from 'src/app/core/services/msteams-shared.service';
import { Util } from 'src/app/core/utils/Util';
import { PwaService } from 'src/app/core/services/pwa.service';
import { Account } from '@mitel/cloudlink-sdk/admin';

const logContext = 'MSTeamsInstallDialogComponent';

@Component({
  selector: 'app-msteams-install-dialog',
  templateUrl: './msteams-install-dialog.component.html',
  styleUrls: ['./msteams-install-dialog.component.scss']
})
export class MSTeamsInstallDialogComponent implements OnInit {
	showFormError: boolean = false;
	errMsg: string = "";
	errMsgStyle: any = { 'max-width': '500px' };
	webAppUpdateAvailable: boolean = false;
	preInstall: boolean = true;
	whileInstalling: boolean = false;
	doneInstall: boolean = false;
	done: number;
	total: number;
	progress: number;

	constructor( @Inject(MAT_DIALOG_DATA) public data: any,
				public dialogRef: MatDialogRef<MSTeamsInstallDialogComponent>,
				private translateService: TranslateService,
				private adminService: AdminService,
				private msteamsSharedService: MSTeamsSharedService,
				private pwaService: PwaService ) 
	{
	}

	async ngOnInit() 
	{
		// nee to know if there is a pending update
		this.webAppUpdateAvailable = this.pwaService.isUpdateAvailable();
	}

	async onInstallUpdate(event) {
		console.log(`${logContext}.onInstallUpdate() Install update starting...`);
		this.showFormError = false;

		this.preInstall = false;
		this.whileInstalling = true;
		
		console.log(`${logContext}.onInstallUpdate() step 1 starting...prep`);
		this.total = 8;
		this.done = 1;
		this.progress = 0;
		return this.adminService.getAccountTags( this.data.account.accountId ).then( async tags => {
			console.log(`${logContext}.onInstallUpdate() step 2 starting...clearing caches`);
			this.done = 2;
			this.progress = 5;
			await Util.timeout(500); // just to see the step
			// clean out the caches
			this.msteamsSharedService.clearUpgradePendingCache( this.data.account );
			this.msteamsSharedService.clearAllMSTeamsToCLPresenceCache();
			this.msteamsSharedService.resetAccountMSTeamsData();

			console.log(`${logContext}.onInstallUpdate() step 3 starting...remove CL to MS Teams workflow`);
			this.done = 3;
			this.progress = 10;
			await Util.timeout(500); // just to see the step

			return this.msteamsSharedService.removeCloudLinkToMSTeamsWorkflow( this.data.account, tags ).then( () => {
				console.log(`${logContext}.onInstallUpdate() step 4 starting...remove MS Teams To CL workflows`);
				this.done = 4;
				this.progress = 20;

				return this.msteamsSharedService.removeMSTeamsToCloudLinkWorkflows( this.data.account, tags ).then( () => {
					console.log(`${logContext}.onInstallUpdate() step 5 starting...re-creating CL to MS Teams WF`);
					this.done = 5;
					this.progress = 35;

					// only create the WFs for the subfeatures where the WF previously existed
					return this.createCloudLinkToMSTeamsWorkflow( this.data.account, tags ).then( CLToMSTeamsWorkflowData => {
						console.log(`${logContext}.onInstallUpdate() step 6 starting...re-creating MS Teams to CL WF`);
						this.done = 6;
						this.progress = 50;

						return this.createMSTeamsToCloudLinkWorkflowsIfRequired( this.data.account, tags ).then(MSTeamsToCLWorkflowData => {
							console.log(`${logContext}.onInstallUpdate() step 7 starting...restore previous configuration CL to MS`);
							this.done = 7;
							this.progress = 80;

							return this.msteamsSharedService.restorePreviousCloudLinkToMSTeamsWorkflowState( this.data.account, tags, CLToMSTeamsWorkflowData ).then( async returnVal => {
								console.log(`${logContext}.onInstallUpdate() step 8 starting...restore previous configuration MS To CL`);
								this.done = 8;
								this.progress = 90;

								return this.msteamsSharedService.restorePreviousMSTeamsToCloudLinkWorkflowState( this.data.account, tags, MSTeamsToCLWorkflowData ).then( async returnVal => {
									this.progress = 100;
									await Util.timeout(500); // just to see the last step
									this.whileInstalling = false;
									this.doneInstall = true;
									console.log(`${logContext}.onInstallUpdate() install/ upgrade complete`);
								})
							});
						});
					});
				})
			})
		})
		.catch( err=> {
			this.displayErrorBasedOnException( err );
			this.whileInstalling = false;
			this.preInstall = true;
		});
	}

	createMSTeamsToCloudLinkWorkflowsIfRequired( account: Account, tags: any )
	{
		if ( tags && tags.MSTeamsToCloudLink && tags.MSTeamsToCloudLink.client ) // only recreate if it existed before
		{
			return this.msteamsSharedService.createMSTeamsToCloudLinkWorkflows( this.data.account, tags ).then(MSTeamsToCLWorkflowData => {
				return MSTeamsToCLWorkflowData;
			});
		}
		else
		return Promise.resolve(null);
	}

	createCloudLinkToMSTeamsWorkflow( account: Account, tags: any )
	{
		if ( tags && tags.MSTeamsPresence && tags.MSTeamsPresence.activityId ) // only recreate if it existed before
		{
			return this.msteamsSharedService.createCloudLinkToMSTeamsWorkflow( this.data.account, tags ).then(CLToMSTeamsWorkflowData => {
				return CLToMSTeamsWorkflowData;
			});
		}
		else
			return Promise.resolve(null);
	}

	onDone(event) 
	{
		let result =
		{
			setupComplete: true,
		}
		this.dialogRef.close(result);
	}

	onCancel(event) {
		this.dialogRef.close( );
	}

	displayErrorBasedOnException( err: any ) {

		console.error("Display this error to the user", err);

        let addMsg = Util.extractMessageFromError(err);
        if ( addMsg )
          this.errMsg = this.translateService.instant('integrations.msteams.install-dialog.error-installing') + 
              ": " + addMsg;
        else
          this.errMsg = this.translateService.instant('integrations.msteams.install-dialog.error-installing');

        this.showFormError = true;
	}
}
